import React, { useCallback, useEffect, useState } from "react";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import providers from "lib/providers";
import BaseAvatar from "components/BaseAvatar";
import "./style.scss";

interface Props {
  userIds: string[];
  query: string;
  onSelect: (user: UserPublicData) => void;
  onClose: () => void;
}

function MentionAutocomplete({ userIds, query, onSelect, onClose }: Props) {
  const [users, setUsers] = useState<UserPublicData[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!userIds || userIds.length === 0) return;
    let cancelled = false;
    Promise.all(
      userIds.map((id) => providers.UserPublicDataProvider.getById({ userId: id }))
    ).then((results) => {
      if (cancelled) return;
      const filtered = results.filter(Boolean).filter((u: UserPublicData) => {
        if (!query) return true;
        return u.screenName?.toLowerCase().includes(query.toLowerCase());
      });
      setUsers(filtered);
      setActiveIndex(0);
    });
    return () => { cancelled = true; };
  }, [userIds, query]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, users.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && users.length > 0) {
        e.preventDefault();
        onSelect(users[activeIndex]);
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [users, activeIndex, onSelect, onClose]);

  if (users.length === 0) return null;

  return (
    <div className="mention-autocomplete">
      {users.map((user, i) => (
        <div
          key={user.id}
          className={`mention-autocomplete__item${i === activeIndex ? " __active" : ""}`}
          onMouseDown={(e) => { e.preventDefault(); onSelect(user); }}
          onMouseEnter={() => setActiveIndex(i)}
        >
          <BaseAvatar user={user} size={28} />
          <span className="mention-autocomplete__name">{user.screenName}</span>
          <span className="mention-autocomplete__hint">@{user.screenName}</span>
        </div>
      ))}
    </div>
  );
}

export default MentionAutocomplete;
